// @owners { team: patients }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  Button,
  Card,
  Description,
  H1,
  Icon,
  ListItem,
  MdPadding,
  Row,
  SecondaryPage,
  SmSpacing,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import pluralize from 'pluralize';
import React, { useEffect } from 'react';
import { clearAuthError, logout } from '~shared/actions/auth';
import { selectUser } from '~shared/actions/ui/users';
// eslint-disable-next-line import/no-deprecated
import { fetchUsers } from '~shared/actions/users';
import { getFamilyMembers } from '~shared/features/family-accounts/selectors/getFamilyMembers';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import { getDisplayFullName } from '~shared/helpers/helper';
import { useAnalytics, useIsFeatureDisabledForCompany, usePageViewedEvent } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { configureStore } from '~web/configureStore';

export const Profile = () => {
  const { navigate } = useNavigation();
  const queryClient = useQueryClient();
  const { trackEvent } = useAnalytics();
  const dispatch = useDispatchShared();
  const users = useSelectorShared(getFamilyMembers);
  const user = useSelectorShared(getCurrentUser);
  const isFamilyMember = user?.is_family_member ?? false;
  const { logout: logoutAuth0 } = useAuth0();
  const { isFeatureDisabled: shouldDisableAddFamilyMember, isLoading: isFeatureFlagLoading } =
    useIsFeatureDisabledForCompany('add_family_member');

  const handleLogout = async () => {
    trackEvent({ event: EVENTS.LOGOUT_CLICKED });
    const success = await dispatch(logout());
    await configureStore().persistor.purge();
    queryClient.clear();
    dispatch(clearAuthError());
    await logoutAuth0({ logoutParams: { returnTo: window.location.origin } });

    if (success) {
      navigate('RouteLogin');
    }
  };

  usePageViewedEvent({ event: EVENTS.PROFILE_VIEWED });

  useEffect(() => {
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchUsers);
  }, [dispatch]);

  return (
    <SecondaryPage
      headerBackgroundColor={COLORS.BACKGROUND_COLORS.GREY_LIGHTEST}
      HeaderContent={
        <MdPadding topPadding={SPACING.STATIC.XXXL}>
          <Row
            spaceBetween
            centerVertically
          >
            <H1>Account</H1>
            <Button
              accessibilityLabel="Log out"
              label="Log out"
              onPress={handleLogout}
              small
              type="secondary"
            />
          </Row>
        </MdPadding>
      }
    >
      <Card
        title="Settings"
        overflow="hidden"
      >
        <ListItem
          LeftContent={<Icon name="mail" />}
          RightContent={<Icon name="chevronright" />}
          title="Contact & Login Info"
          descriptions={<Description>Email, Password & Phone Number</Description>}
          onPress={() => {
            navigate('RouteContactInfo');
          }}
        />
        <ListItem
          LeftContent={<Icon name="sync" />}
          RightContent={<Icon name="chevronright" />}
          title="Auto Refill"
          onPress={() => {
            navigate('RouteAutoRefillManagement');
          }}
        />
        <ListItem
          LeftContent={<Icon name="creditcard" />}
          RightContent={<Icon name="chevronright" />}
          title="Payments and Receipts"
          onPress={() => {
            navigate('RoutePayments');
          }}
        />
        <ListItem
          LeftContent={<Icon name="mapmarker" />}
          RightContent={<Icon name="chevronright" />}
          title="Delivery Addresses"
          onPress={() => {
            navigate('RouteAddresses');
          }}
        />
      </Card>
      <SmSpacing />
      <Card
        title={pluralize('Profile', users.length)}
        overflow="hidden"
      >
        {users.map((user) => {
          return (
            <ListItem
              key={user.id}
              LeftContent={<Icon name="usercircle" />}
              RightContent={<Icon name="chevronright" />}
              title={getDisplayFullName(user)}
              descriptions={<Description>Personal & Insurance Info</Description>}
              onPress={() => {
                dispatch(selectUser(user.id));
                navigate('RouteUserProfile', { userID: user.id });
              }}
            />
          );
        })}
        {isFamilyMember || shouldDisableAddFamilyMember || isFeatureFlagLoading ? null : (
          <ListItem
            LeftContent={<Icon name="userplus" />}
            RightContent={<Icon name="chevronright" />}
            title="Add a family member"
            onPress={() => {
              navigate('RouteAddFamilyMember');
            }}
          />
        )}
      </Card>
    </SecondaryPage>
  );
};
